import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar, FaMountain } from 'react-icons/fa';
import { companyInfo } from '../../data/tourData';
import BookingForm from '../../components/common/BookingForm';
import './TourDetail.css';

const YullaKanda = () => {
  const tourData = {
    name: "Yulla Kanda",
    duration: "03 Nights 04 Days",
    price: "₹ 6,499/-",
    image: "https://res.cloudinary.com/duym2bgp2/image/upload/v1755615432/jwcdntyka58ccbp87rxz.jpg",
    altitude: "12,000 ft",
    difficulty: "Moderate",
    region: "Himachal Pradesh",
    highlights: ["Trekking adventure", "Yulla Kanda Lake", "Mountain views"],
    bestTime: "May to October",
    description: "The Yulla Kanda trek is an exciting adventure in Himachal Pradesh that combines trekking with spiritual experiences. This moderate trek offers lake visits, temple visits, and stunning mountain views, making it perfect for adventure enthusiasts."
  };

  return (
    <>
      <Helmet>
        <title>Yulla Kanda Trek - NomadTrailz</title>
        <meta name="description" content="Experience the beautiful Yulla Kanda trek in Himachal Pradesh. Adventure trek with lake visits and scenic beauty." />
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <section className="tour-hero">
          <div className="hero-background">
            <img
              src={tourData.image}
              alt={tourData.name}
            />
            <div className="hero-overlay"></div>
          </div>
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {tourData.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Adventure trek with lake visits and scenic beauty
            </motion.p>
          </div>
        </section>

        <section className="tour-details">
          <div className="container">
            <div className="details-grid">
              <div className="tour-info">
                <motion.div
                  className="info-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2>Tour Overview</h2>
                  <p>{tourData.description}</p>
                  <p>
                    This trek takes you to the beautiful Yulla Kanda Lake in Himachal Pradesh, 
                    offering a perfect blend of adventure and spiritual experiences. The journey 
                    includes scenic drives, trekking, temple visits, and stunning mountain views, 
                    making it an ideal adventure escape.
                  </p>
                </motion.div>

                <motion.div
                  className="highlights-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Highlights</h3>
                  <ul>
                    <li>Trekking adventure</li>
                    <li>Yulla Kanda Lake visit</li>
                    <li>Shri Krishna Temple</li>
                    <li>Mountain views</li>
                    <li>Kinnaur district exploration</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Fun-filled journey</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="itinerary-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Itinerary</h3>
                  <div className="day-item">
                    <h4>Day 0: Departure from Delhi</h4>
                    <ul>
                      <li>Depart at 10:00 PM in a luxury tempo traveler</li>
                      <li>Enjoy a fun-filled overnight ride with Antakshari, Dumb Charades, Dance-offs, and plenty of games</li>
                      <li>Dinner en route</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 1: Delhi → Shimla → Kinnaur → Tapri</h4>
                    <ul>
                      <li>Greet the day with a cheerful "Subah Ho Gayi Yaar!"</li>
                      <li>Arrive at Shimla and continue on a 7-8 hour scenic drive to Kinnaur district, via Narkanda and Rampur Bushahr</li>
                      <li>Reach your stay in Tapri, freshen up, and unwind</li>
                      <li>Dinner & overnight stay under the stars</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 2: Trek to Yulla Kanda Base</h4>
                    <ul>
                      <li>Early morning wake-up and hearty breakfast</li>
                      <li>Begin the 12 km trek to Yulla Kanda Lake</li>
                      <li>The trek takes around 4-5 hours</li>
                      <li>After about 10.5 km, take a break and rest at the base campsite</li>
                      <li>Enjoy a warm dinner and overnight stay surrounded by Himalayan serenity</li>
                    </ul>
                  </div>
                  <div className="day-item">
                    <h4>Day 3: Temple Visit & Return Trek</h4>
                    <ul>
                      <li>Wake up early and have breakfast</li>
                      <li>Visit the Shri Krishna Temple, take in the majestic views and soak in the spiritual vibe</li>
                      <li>Trek back downhill to Tapri Village</li>
                      <li>Drive to Shimla</li>
                      <li>Begin your return journey to Delhi in the evening</li>
                      <li>Reach back by the next morning with memories of a lifetime!</li>
                    </ul>
                  </div>
                </motion.div>

                <motion.div
                  className="inclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Inclusions</h3>
                  <ul>
                    <li>Transportation from Delhi to Delhi: comfortable AC Deluxe Bus/Tempo Traveler</li>
                    <li>2 Nights Accommodation on sharing basis: Resort/Cottages</li>
                    <li>All Meals: Dinner (2), Breakfast (2) & Evening Snacks (1), starting from Dinner on Day 0 to Breakfast on Day 3</li>
                    <li>Trekking equipment and guide</li>
                    <li>Shri Krishna Temple visit</li>
                    <li>Experienced and cool Trip Captain</li>
                    <li>Driver allowances, toll taxes, parking, state taxes</li>
                    <li>24x7 Customer Support</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="exclusions-card"
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Exclusions</h3>
                  <ul>
                    <li>5% GST</li>
                    <li>Any meal/food item other than the package inclusions</li>
                    <li>Any entry ticket to monuments & public places</li>
                    <li>Personal trekking gear and equipment</li>
                    <li>Travel Insurance and other benefits</li>
                    <li>Medical expenses (apart from first aid) and insurance of any kind</li>
                    <li>Tips, laundry, liquors, wines, mineral water, telephone charges, camera fee and items of personal nature</li>
                    <li>Anything which is not mentioned in inclusions</li>
                    <li>Any extra cost that occurs due to natural calamity or any such unseen situation that is beyond control</li>
                  </ul>
                </motion.div>
              </div>

              <div className="booking-section">
                <motion.div
                  className="booking-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h3>Book This Tour</h3>
                  <div className="price-info">
                    <span className="price">{tourData.price}</span>
                    <span className="duration">{tourData.duration}</span>
                  </div>

                  <div className="tour-features">
                    <div className="feature">
                      <FaMapMarkerAlt />
                      <span>{tourData.region}</span>
                    </div>
                    <div className="feature">
                      <FaCalendarAlt />
                      <span>Best Time: {tourData.bestTime}</span>
                    </div>
                    <div className="feature">
                      <FaUsers />
                      <span>Group Size: 8-12 people</span>
                    </div>
                    <div className="feature">
                      <FaStar />
                      <span>Difficulty: {tourData.difficulty}</span>
                    </div>
                    <div className="feature">
                      <FaMountain />
                      <span>Altitude: {tourData.altitude}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="quick-info-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <h3>Quick Info</h3>
                  <div className="info-item">
                    <FaMountain />
                    <div>
                      <strong>Max Altitude:</strong>
                      <span>12,000 feet</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>Trek Distance:</strong>
                      <span>24 km (round trip)</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaStar />
                    <div>
                      <strong>Difficulty Level:</strong>
                      <span>Moderate</span>
                    </div>
                  </div>
                  <div className="info-item">
                    <FaCalendarAlt />
                    <div>
                      <strong>Best Season:</strong>
                      <span>May to October</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  className="cost-details-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <h3>Cost Details</h3>
                  <h4>Cost Per Person</h4>
                  <ul>
                    <li><strong>TRIPLE SHARING:</strong> ₹ 6,499 + 5% GST</li>
                    <li><strong>DOUBLE SHARING:</strong> ₹ 6,999 + 5% GST</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="pickup-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  <h3>Pickup Points</h3>
                  <ul>
                    <li><strong>Delhi:</strong> ISBT Kashmere Gate b/w 09:00 PM to 10:00 PM</li>
                  </ul>
                </motion.div>

                <motion.div
                  className="tour-highlights-card"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <h3>Tour Highlights</h3>
                  <ul>
                    <li>Trekking adventure</li>
                    <li>Yulla Kanda Lake visit</li>
                    <li>Shri Krishna Temple</li>
                    <li>Mountain views</li>
                    <li>Kinnaur district exploration</li>
                    <li>Moderate difficulty level</li>
                    <li>Perfect for adventure enthusiasts</li>
                    <li>Fun-filled journey</li>
                    <li>Trip captain support</li>
                  </ul>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <BookingForm 
                  tourName={tourData.name}
                  tourPrice={tourData.price}
                  tourDuration={tourData.duration}
                />
              </motion.div>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};

export default YullaKanda;
